/**
 * Normalized object-AST builders for virtual-view expansions — the engine's
 * `(op …params)` / `{$kw}` / `{$sym}` shape, so an expansion is exactly what
 * an agent would write by hand. SSOT for every facade/reshape factory under
 * `instructions/`.
 */
/** biome-ignore-all lint/style/useNamingConvention: internal names */

import type { AstInput } from '#core/lib/expr/normalize.ts';
import type { Opcode } from '#core/lib/expr/op-surface.ts';

/** An op-call node `(name …params)`. `name` is checked against the canonical op surface. */
export const op = (name: Opcode, ...params: readonly AstInput[]): AstInput => ({ name, params });
/** A keyword `:k`. */
export const kw = (k: string): AstInput => ({ $kw: k });
/** The row alias symbol `%`. */
export const ROW: AstInput = { $sym: '%' };
/**
 * A row column reference `(col % :col)`, with an optional struct sub-path
 * `(col % :col :sub)`. SSOT for every row-scoped column ref in the
 * virtual-view expansions.
 */
export const colRef = (col: string, sub?: string): AstInput =>
  sub === undefined ? op('col', ROW, kw(col)) : op('col', ROW, kw(col), kw(sub));
/** A single-`%`-param lambda `#(<body>)`. */
export const lambda = (body: AstInput): AstInput => op('fn', [ROW], body);
/** A map literal `{:k v …}` from ordered key→value entries. */
export const mapLit = (entries: readonly (readonly [string, AstInput])[]): AstInput => ({
  $map: entries.map(([k, v]) => ({ k, v })),
});
/** A struct-construction `(struct :member expr …)` from member→value-expr entries. */
export const structExpr = (members: readonly (readonly [string, AstInput])[]): AstInput =>
  op('struct', ...members.flatMap(([member, value]) => [kw(member), value]));
/** A row-predicate equality `(= (:col %) value)` — the common `where` shape. */
export const eq = (col: string, value: AstInput): AstInput => op('=', colRef(col), value);
